import { TooltipButton } from './common/TooltipButton';
import React, { useState, useEffect, useMemo } from 'react';
import { X, Check, ChevronDown, Camera, Loader2, Trash2, ShoppingBag, Plus, Minus, Fingerprint, BrainCircuit, Calendar as CalendarIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CategoryInfo, ShoppingItem } from '../types';
import { ConfirmationModal } from './common/ConfirmationModal';
import { SmartImage } from './common/SmartImage';
import { useTranslation } from 'react-i18next';
import { constructProtocolSlug, generateItemIdentity, destructureName } from '../src/lib/intelligence';
import { UnitPriceCheckPanel } from './common/UnitPriceCheckPanel';
import { sizeFixUpdates } from '../src/lib/basketItem';
import { generateEmbedding } from '../services/geminiService';
import { ExpirationDatePicker } from './ExpirationDatePicker';
import { useSupabase } from '../src/lib/SupabaseContext';
import { useStore } from '../src/lib/store';

interface EditItemModalProps {
  item: ShoppingItem | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (id: string, updates: Partial<ShoppingItem>) => void;
  onRemove: (id: string) => void;
  categories: CategoryInfo[];
  currencySymbol: string;
  /** Embed the same form in a receipt review instead of opening an overlay. */
  inline?: boolean;
  onImageUpload?: (id: string, file: File | string) => Promise<void>;
}

export const EditItemModal: React.FC<EditItemModalProps> = ({
  item,
  isOpen,
  onClose,
  onUpdate,
  onRemove,
  categories,
  currencySymbol,
  onImageUpload,
  inline = false,
}) => {
  // RULES OF HOOKS: No returns before hooks. No conditional hooks.
  const { t, i18n } = useTranslation();
  
  // Structured Identity States
  const [brand, setBrand] = useState<string | null>(null);
  const [product, setProduct] = useState('');
  const [variety, setVariety] = useState<string | null>(null);
  const [size, setSize] = useState<number | string | null>(null);
  const [unit, setUnit] = useState<string | null>(null);
  const [upc, setUpc] = useState<string | null>(null);
  const [gtin, setGtin] = useState<string | null>(null);
  
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [category, setCategory] = useState('');
  const [canonicalId, setCanonicalId] = useState('');
  const [embedding, setEmbedding] = useState<number[] | null>(null);
  const [shelfLife, setShelfLife] = useState<number | null>(null);
  const [expirationDate, setExpirationDate] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<Record<string, any> | null>(null);
  
  const [showCatMenu, setShowCatMenu] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  useEffect(() => { setShowDetails(false); }, [isOpen, item?.id]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const supabase = useSupabase();
  const userProfile = useStore(s => s.userProfile);
  const unitSystem = useStore(s => s.unitSystem);
  const massUnits = useStore(s => s.massUnits);

  // Helper: get YYYY-MM-DD local
  const toLocalISO = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper: format YYYY-MM-DD display
  const formatDisplayDate = (dateStr: string | null) => {
    if (!dateStr) return t('common.unknown');
    try {
      const [y, m, d] = dateStr.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString();
    } catch (e) {
      return t('common.unknown');
    }
  };

  useEffect(() => {
    if (item) {
      // Clean 'Verified' badge noise if present in name
      const cleanedName = (item.name || '').replace(/\s*Verified\s*$/i, '').trim();

      // AUTO-DESTRUCTURE FALLBACK: If both brand and product are missing in the data, 
      // try to extract them from the name (Sovereign Protocol).
      let initialBrand = item.brand || null;
      let initialProduct = item.product || '';
      
      if (!initialBrand && !initialProduct) {
        const parts = destructureName(cleanedName);
        initialBrand = parts.brand;
        initialProduct = parts.product;
      }

      setBrand(initialBrand);
      setProduct(initialProduct); 
      setVariety(item.variety || null);
      
      // Reset structure states to avoid bleed from previous item
      setUnit(item.unit || null);
      
      // UNIFIED SIZE PARSING: Handle '8oz' or '1.5lb' strings
      let resolvedSize: number | string | null = typeof item.size === 'number' ? item.size : null;
      let resolvedUnit: string | null = item.unit || null;

      if (typeof item.size === 'string' && !resolvedSize) {
        const match = item.size.match(/^([\d.]+)\s*([a-zA-Z\s]+)$/);
        if (match) {
          resolvedSize = parseFloat(match[1]);
          const rawUnit = match[2].trim().toLowerCase();
          resolvedUnit = (rawUnit === 'ea' || rawUnit === 'each') ? 'ct' : rawUnit;
        } else {
          // Alpha size fallback
          resolvedSize = item.size;
          // Maintain original unit if available
          resolvedUnit = item.unit || null;
        }
      }

      setSize(resolvedSize);
      setUnit(resolvedUnit || item.unit || null);
      setUpc(item.upc || null);
      setGtin(item.gtin || null);
      setPrice(item.price?.toString() || '0');
      setCategory(item.category?.trim() || 'Other');
      setQuantity(item.quantity || 1);
      setCanonicalId(item.canonicalId || '');
      setEmbedding(item.embedding || null);
      setMetadata((item as any).metadata || null);
      
      const initialShelfLife = item.shelfLife ?? null;
      setShelfLife(initialShelfLife);

      // PIVOT: If expirationDate is missing/invalid but shelfLife exists, calculate it
      let resolvedExpDate = item.expirationDate;
      if (!resolvedExpDate || isNaN(new Date(resolvedExpDate).getTime())) {
        if (initialShelfLife !== null) {
          const date = inline ? new Date((item as any).created_at || new Date()) : new Date();
          date.setDate(date.getDate() + initialShelfLife);
          resolvedExpDate = toLocalISO(date);
        }
      }
      setExpirationDate(resolvedExpDate || null);
    }
    // Keyed on item.id, not the whole item object: this hydrates the form when
    // switching to a genuinely different item. Depending on the object itself
    // meant any unrelated update to the same item (e.g. handleImageChange's own
    // onUpdate(item.id, { imageUrl }) call after saving the new photo) produced a
    // new item reference, re-ran this effect, and stomped the fields Gemini had
    // just populated a few seconds later with the stale pre-extraction values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  // AUTO-PROTOCOL: Rebuild ID on change
  useEffect(() => {
    if (product) {
      const newSlug = constructProtocolSlug({ brand, product, variety, size, unit });
      if (newSlug !== canonicalId) setCanonicalId(newSlug);
    }
  }, [brand, product, variety, size, unit, canonicalId]);

  const currentCategory = useMemo<CategoryInfo>(() =>
    categories.find(c => c.name.trim().toLowerCase() === category.trim().toLowerCase()) || categories[0] ||
      { name: t('categories.other'), color: '#94a3b8', emoji: '📦', translationKey: 'categories.other' }
  , [categories, category, t]);

  // Stock/product photo first (nicer, what this card is mainly about), then the raw scan
  // (receipt or tag — proof of purchase) as a second slide when both exist.
  const carouselImages = useMemo(() => (
    [(metadata as any)?.stockImageUrl, item?.imageUrl].filter((u): u is string => !!u)
  ), [metadata, item?.imageUrl]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  useEffect(() => { setCarouselIndex(0); }, [item?.id]);
  const activeImageUrl = carouselImages[Math.min(carouselIndex, carouselImages.length - 1)];

  const { name: displayName } = useMemo(() => 
    generateItemIdentity({ brand, product, variety, size, unit })
  , [brand, product, variety, size, unit]);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const { name: finalName, canonicalId: finalSlug } = generateItemIdentity({ brand, product, variety, size, unit });
    const itemId = item!.id;
    const priorName = item!.name;
    // Size is typed as text ("500" or "XL"); store numbers as numbers.
    const sizeValue = typeof size === 'string' && /^\d+(\.\d+)?$/.test(size.trim()) ? parseFloat(size) : size;
    const finalPrice = Math.max(0, parseFloat(price) || 0);
    const priorMetadata = metadata;
    // The embedding is the item's Lattice search index for its name. When the
    // name changes (or there is none), save without it and make a fresh one in
    // the background, so a renamed item isn't matched as the old product.
    const nameChanged = finalName !== priorName;
    const needsEmbedding = nameChanged || !embedding;

    onUpdate(itemId, {
      name: finalName,
      brand,
      product,
      variety,
      size: sizeValue,
      unit,
      upc,
      gtin,
      price: finalPrice,
      category: category,
      quantity: quantity,
      canonicalId: finalSlug,
      embedding: needsEmbedding ? null : embedding,
      shelfLife,
      expirationDate,
      metadata
    } as any);
    onClose();

    // A review draft is saved atomically by its parent; avoid late enrichment writes.
    if (inline) return;

    if (needsEmbedding && finalName) {
      generateEmbedding(finalName, undefined).then((emb) => {
        if (!emb || emb.length === 0) return;
        const { items, latticeItems, operatingContext } = useStore.getState();
        const live = (operatingContext === 'personnel' ? latticeItems : items).find(i => i.id === itemId);
        if (live && !live.embedding) onUpdate(itemId, { embedding: emb });
      }).catch((err) => console.warn('[EditItemModal] embedding refresh failed (non-fatal):', err));
    }

    // A field edit can change what the item actually IS (e.g. correcting brand/product) without
    // the user re-uploading a photo — re-run the stock-photo lookup against the reconstructed
    // nomenclature name so the image doesn't stay stuck showing the pre-edit item. Skip it when
    // the name didn't actually change (price/quantity-only edits) to avoid a wasted search.
    if (finalName && finalName !== priorName) {
      import('../src/lib/marketDiscovery').then(({ fetchProductImages }) =>
        fetchProductImages([{ name: finalName, brand }])
      ).then((images) => {
        const url = images[finalName];
        if (url) onUpdate(itemId, { metadata: { ...(priorMetadata || {}), stockImageUrl: url } } as any);
      }).catch((err) => console.warn('[EditItemModal] Stock image refresh on save failed (non-fatal):', err));
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !item) return;

    setIsUploading(true);
    // Keep the current fields: if the photo can't be read, they are put back
    // instead of leaving an empty form that would wipe the item on Save.
    const snapshot = { brand, product, variety, size, unit, upc, gtin, shelfLife, expirationDate, price, embedding, metadata, category };
    const restore = () => {
      setBrand(snapshot.brand); setProduct(snapshot.product); setVariety(snapshot.variety); setSize(snapshot.size); setUnit(snapshot.unit);
      setUpc(snapshot.upc); setGtin(snapshot.gtin); setShelfLife(snapshot.shelfLife); setExpirationDate(snapshot.expirationDate);
      setPrice(snapshot.price); setEmbedding(snapshot.embedding); setMetadata(snapshot.metadata); setCategory(snapshot.category);
    };
    // Clear stale fields while the photo is read (same as AddProductModal / handleManualImageExtraction)
    setBrand(null); setProduct(''); setVariety(null); setSize(null); setUnit(null);
    setUpc(null); setGtin(null);
    setShelfLife(null); setExpirationDate(null);
    setPrice('0'); setEmbedding(null); setMetadata(null);

    try {
      const { processImage } = await import('../services/imageUtils');
      const { base64, blob } = await processImage(file, 1000);

      const { ImageCacheService } = await import('../services/imageCacheService');
      // Only delete old local image if no other basket item shares the same URL
      if (item.imageUrl?.startsWith('local:')) {
        const { useStore: getStore } = await import('../src/lib/store');
        const allItems = getStore.getState().items;
        const stillUsed = allItems.some(i => i.id !== item.id && i.imageUrl === item.imageUrl);
        if (!stillUsed) await ImageCacheService.removeImage(item.imageUrl);
      }
      const localUrl = await ImageCacheService.saveImage(blob);
      onUpdate(item.id, { imageUrl: localUrl });

      // Re-run Gemini extraction exactly like handleManualImageExtraction in App.tsx
      const { extractItemsFromImage } = await import('../services/geminiService');
      const result = await extractItemsFromImage(
        supabase!,
        base64,
        categories,
        'tag',
        userProfile?.ai_profile || undefined,
        [],
        unitSystem,
        massUnits,
        i18n.language,
      );

      if (!result.isReceipt && result.items?.length) {
        const ex = result.items[0];
        if (ex.brand !== undefined) setBrand(ex.brand || null);
        if (ex.product) setProduct(ex.product);
        if (ex.variety !== undefined) setVariety(ex.variety || null);
        if (ex.size !== undefined && ex.size !== null) setSize(ex.size);
        if (ex.unit) setUnit(ex.unit);
        if (ex.price) setPrice(ex.price.toString());
        if (ex.category) setCategory(ex.category);
        setShelfLife(ex.shelfLife || null);
        if (ex.shelfLife) {
          const d = new Date();
          d.setDate(d.getDate() + ex.shelfLife);
          setExpirationDate(toLocalISO(d));
        }
        setUpc(ex.upc || null);
        setGtin(ex.gtin || null);
        // The new photo's shelf unit-price check (if its tag prints a unit price).
        const unitPriceCheck = ex.metadata?.unit_price_check;
        if (unitPriceCheck) setMetadata((prev) => ({ ...(prev || {}), unit_price_check: unitPriceCheck }));

        // Best-effort stock photo — localUrl (just saved above) stays the actual proof shot.
        const lookupName = (ex as any).name || ex.product;
        if (lookupName) {
          import('../src/lib/marketDiscovery').then(({ fetchProductImages }) =>
            fetchProductImages([{ name: lookupName, brand: ex.brand }])
          ).then((images) => {
            const url = images[lookupName];
            if (url) setMetadata((prev) => ({ ...(prev || {}), stockImageUrl: url }));
          }).catch((imgErr) => console.warn('[EditItemModal] stock image lookup failed (non-fatal):', imgErr));
        }
      } else {
        restore();
      }
    } catch (error) {
      console.error('[EditItemModal] Image change / re-extraction failed:', error);
      restore();
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={inline ? "lattice-item-editor min-w-0" : "fixed inset-0 z-[400] flex items-center justify-center p-2 sm:p-4 pointer-events-none"}>
      <AnimatePresence>
        {(isOpen && item) && (
          <div className={inline ? "min-w-0" : "fixed inset-0 flex items-center justify-center p-2 sm:p-4 pointer-events-auto"}>
            {!inline && <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-[#1a2b3c]/60 backdrop-blur-md"
            />}
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className={inline ? "relative w-full rounded-[2rem] border border-white/10 overflow-hidden flex flex-col bg-[#111c2d]" : "relative bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"}
            >
              {/* Header */}
              <div className="px-6 py-4 flex justify-between items-center bg-gray-50/50 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-600 rounded-xl shadow-md flex items-center justify-center text-white">
                    <ShoppingBag size={20} strokeWidth={2.5} />
                  </div>
                  <div>
                    <h3 className="text-lg font-black uppercase text-[#1a2b3c] tracking-tighter leading-none">{t('product.edit_item')}</h3>
                    <p className="text-[8px] font-black text-indigo-600 uppercase tracking-widest mt-1">{t('product.identity_management')}</p>
                  </div>
                </div>
                <TooltipButton label={t('common.cancel', { defaultValue: 'Cancel' })} type="button" aria-label={t('common.cancel', { defaultValue: 'Cancel' })} onClick={onClose} className="p-2 bg-gray-100 text-gray-400 rounded-full hover:bg-gray-200 transition-all active:scale-90">
                  <X size={20} />
                </TooltipButton>
              </div>

              <form onSubmit={handleSave} className="flex-1 flex flex-col min-h-0">
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 scrollbar-hide">
                  
                  {/* Visual Identity Card */}
                  <div className="bg-[#1a2b3c] rounded-[2rem] p-5 text-white shadow-xl relative overflow-hidden group">
                    <div className="absolute top-[-10%] right-[-10%] w-32 h-32 bg-indigo-500/10 blur-3xl rounded-full" />
                    <div className="relative z-10 flex gap-4 items-center">
                      <div className="relative w-20 h-20 shrink-0">
                        <TooltipButton label={t('product.next_image', { defaultValue: 'Next image' })}
                          type="button"
                          disabled={isUploading || carouselImages.length < 2}
                          onClick={() => setCarouselIndex((i) => (i + 1) % carouselImages.length)}
                          className={`w-20 h-20 bg-white/5 rounded-2xl overflow-hidden border border-white/10 flex items-center justify-center transition-all ${carouselImages.length > 1 ? 'hover:bg-white/10 cursor-pointer' : ''} ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {isUploading ? (
                            <Loader2 className="animate-spin text-emerald-400" size={24} />
                          ) : activeImageUrl ? (
                            <SmartImage key={activeImageUrl} imageUrl={activeImageUrl} alt={item?.name} className="w-full h-full object-cover" />
                          ) : (
                            <Camera size={24} className="text-white/20" />
                          )}
                        </TooltipButton>

                        {carouselImages.length > 1 && (
                          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex items-center gap-1 pointer-events-none">
                            {carouselImages.map((_, i) => (
                              <span
                                key={i}
                                className={`w-1.5 h-1.5 rounded-full transition-all ${i === carouselIndex ? 'bg-white' : 'bg-white/30'}`}
                              />
                            ))}
                          </div>
                        )}

                        <TooltipButton label={t('product.add_photo', { defaultValue: 'Add Photo' })}
                          type="button"
                          disabled={isUploading || inline}
                          onClick={() => fileInputRef.current?.click()}
                          aria-label={t('product.add_photo')}
                          className="absolute -bottom-1.5 -right-1.5 w-7 h-7 bg-emerald-500 rounded-full shadow-lg flex items-center justify-center border-2 border-[#1a2b3c] hover:bg-emerald-400 transition-colors active:scale-90 disabled:opacity-50"
                        >
                          <Camera size={12} className="text-white" />
                        </TooltipButton>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[8px] font-black text-indigo-400 uppercase tracking-widest mb-1">{t('product.display_preview')}</p>
                        <h4 className="text-lg font-black tracking-tight leading-snug break-words">
                          {displayName || t('product.product_placeholder')}
                        </h4>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <div className="flex items-center gap-1.5">
                            <Fingerprint size={10} className="text-white/30" />
                            <span className="text-[9px] font-bold text-white/40 font-mono tracking-tighter truncate max-w-[120px]">
                              {canonicalId}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageChange} />
                  </div>

                  {/* Identity Fields */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest px-1">{t('product.brand_label')}</label>
                        <input 
                          className="w-full bg-[#1a2b3c]/5 rounded-xl px-4 py-3 text-sm font-bold text-[#1a2b3c] outline-none border-2 border-transparent focus:border-emerald-500/20"
                          aria-label={t('product.brand_label')}
                          value={brand || ''} 
                          onChange={e => setBrand(e.target.value || null)} 
                          placeholder={t('item_form.brand_placeholder', { defaultValue: 'e.g. Heinz, Kirkland' })}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest px-1">{t('product.product_label')}</label>
                        <input 
                          required
                          className="w-full bg-[#1a2b3c]/5 rounded-xl px-4 py-3 text-sm font-bold text-[#1a2b3c] outline-none border-2 border-transparent focus:border-emerald-500/20"
                          aria-label={t('product.product_label')}
                          value={product} 
                          onChange={e => setProduct(e.target.value)} 
                          placeholder={t('product.product_placeholder')}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest px-1">{t('product.variety_label')}</label>
                    <input 
                      className="w-full bg-[#1a2b3c]/5 rounded-xl px-4 py-3 text-sm font-bold text-[#1a2b3c] outline-none border-2 border-transparent focus:border-emerald-500/20"
                      aria-label={t('product.variety_label')}
                          value={variety || ''}
                      onChange={e => setVariety(e.target.value || null)} 
                      placeholder={t('product.variety_placeholder')}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1 col-span-1">
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest px-1">{t('product.size_label')}</label>
                      <input 
                        type="text"
                        className="w-full bg-[#1a2b3c]/5 rounded-xl px-4 py-3 text-sm font-bold text-[#1a2b3c] outline-none border-2 border-transparent focus:border-emerald-500/20"
                        aria-label={t('product.size_label')}
                          value={size ?? ''}
                        onChange={e => setSize(e.target.value || null)}
                        placeholder={t('product.size_placeholder')}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest px-1">{t('product.unit_label')}</label>
                      <input 
                        className="w-full bg-[#1a2b3c]/5 rounded-xl px-4 py-3 text-sm font-bold text-[#1a2b3c] outline-none border-2 border-transparent focus:border-emerald-500/20"
                        aria-label={t('product.unit_label')}
                          value={unit || ''}
                        onChange={e => setUnit(e.target.value || null)} 
                        placeholder={t('product.unit_placeholder')}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest px-1">{t('product.qty_label_short')}</label>
                      <div className="flex items-center bg-[#1a2b3c]/5 rounded-xl px-2 py-1.5 border-2 border-transparent">
                        <TooltipButton label={t('product.decrease_quantity', { defaultValue: 'Decrease quantity' })} type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-1.5 hover:bg-white rounded-lg text-[#1a2b3c]"><Minus size={14} /></TooltipButton>
                        <span className="flex-1 text-center font-black text-sm text-[#1a2b3c]">{quantity}</span>
                        <TooltipButton label={t('product.increase_quantity', { defaultValue: 'Increase quantity' })} type="button" onClick={() => setQuantity(quantity + 1)} className="p-1.5 hover:bg-white rounded-lg text-emerald-500"><Plus size={14} /></TooltipButton>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest px-1">{t('product.price_label')}</label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500 font-black text-sm">{currencySymbol}</div>
                        <input
                          required type="number" step="0.01" min="0" inputMode="decimal" aria-label={t('product.price_label')}
                          className="w-full bg-[#1a2b3c]/5 rounded-xl pl-10 pr-4 py-3 text-base font-black text-[#1a2b3c] outline-none border-2 border-transparent focus:border-emerald-500/20"
                          value={price} onChange={e => setPrice(e.target.value)}
                        />
                      </div>
                    </div>
                      <div className="space-y-1 relative">
                        <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest px-1">{t('product.category_label')}</label>
                        <button
                          type="button"
                          onClick={() => setShowCatMenu(!showCatMenu)}
                          className="w-full flex items-center justify-between px-4 py-3 bg-[#1a2b3c]/5 rounded-xl text-xs font-black text-[#1a2b3c] border-2 border-transparent uppercase tracking-tight"
                        >
                          <span className="flex items-center gap-2 truncate">
                            <span>{currentCategory.emoji}</span>
                            <span className="truncate">
                              {currentCategory.translationKey ? t(currentCategory.translationKey) : currentCategory.name}
                            </span>
                          </span>
                          <ChevronDown size={16} className={`text-[#1a2b3c]/30 transition-transform ${showCatMenu ? 'rotate-180' : ''}`} />
                        </button>

                        <AnimatePresence>
                          {showCatMenu && (
                            <>
                              <div className="fixed inset-0 z-[490]" onClick={() => setShowCatMenu(false)} />
                              <motion.div
                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                className="absolute left-0 right-0 bottom-full mb-2 bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden z-[500]"
                              >
                                <div className="p-2 grid grid-cols-1 gap-1 max-h-[300px] overflow-y-auto scrollbar-hide">
                                  {categories.map((cat) => (
                                    <button
                                      key={cat.name}
                                      type="button"
                                      onClick={() => {
                                        setCategory(cat.name);
                                        setShowCatMenu(false);
                                      }}
                                      className={`flex items-center gap-3 w-full px-4 py-3 rounded-2xl transition-all text-left ${category === cat.name ? 'bg-emerald-50 text-emerald-600' : 'hover:bg-gray-50 text-gray-700'}`}
                                    >
                                      <span className="text-xl">{cat.emoji}</span>
                                      <span className="text-[10px] font-black uppercase tracking-tight">
                                        {cat.translationKey ? t(cat.translationKey) : cat.name}
                                      </span>
                                      {category === cat.name && <Check size={16} className="ml-auto" />}
                                    </button>
                                  ))}
                                </div>
                              </motion.div>
                            </>
                          )}
                        </AnimatePresence>
                      </div>
                  </div>

                  {/* The shelf unit-price check on the values as typed. "Looks right"
                      fills in the size the tag implies; Update saves it. */}
                  {(() => {
                    const typedSize = typeof size === 'string' && /^\d+(\.\d+)?$/.test(size.trim()) ? parseFloat(size) : size;
                    const draft = { ...item!, brand, product, variety, size: typedSize, unit, price: parseFloat(price) || 0, metadata } as ShoppingItem;
                    return (
                      <UnitPriceCheckPanel
                        item={draft}
                        currencySymbol={currencySymbol}
                        onLooksRight={() => {
                          const fix = sizeFixUpdates(draft);
                          if (!fix) return;
                          setSize(fix.size);
                          setUnit(fix.unit);
                        }}
                      />
                    );
                  })()}

                  {/* Barcodes, expiry and shelf life sit folded under More details. */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setShowDetails(v => !v)}
                      className="w-full flex items-center justify-between px-1 text-[10px] font-black text-gray-400 uppercase tracking-widest"
                    >
                      <span>{t('item_form.more_details', { defaultValue: 'More details' })}</span>
                      <ChevronDown size={16} className={`transition-transform ${showDetails ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence initial={false}>
                      {showDetails && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="pt-4 space-y-6">
                              {/* Sovereign Identifiers (UPC/GTIN) */}
                              <div className="p-5 bg-gray-50/50 rounded-[2rem] border border-gray-100/50 space-y-4">
                                <div className="flex items-center gap-2 mb-1">
                                  <Fingerprint size={14} className="text-[#1a2b3c]/40" />
                                  <h5 className="text-[10px] font-black text-[#1a2b3c]/60 uppercase tracking-widest">{t('product.upc_gtin_section', 'Sovereign Identifiers')}</h5>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-1">
                                    <label className="text-[8px] font-black text-gray-400 uppercase tracking-widest px-1">{t('product.upc_label')}</label>
                                    <input 
                                      className="w-full bg-white rounded-xl px-4 py-2 text-xs font-bold text-[#1a2b3c] outline-none border-2 border-transparent focus:border-emerald-500/20"
                                      aria-label={t('product.upc_label', { defaultValue: 'UPC' })}
                                      value={upc || ''} 
                                      onChange={e => setUpc(e.target.value || null)} 
                                      placeholder="000000000000"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-[8px] font-black text-gray-400 uppercase tracking-widest px-1">{t('product.gtin_label')}</label>
                                    <input 
                                      className="w-full bg-white rounded-xl px-4 py-2 text-xs font-bold text-[#1a2b3c] outline-none border-2 border-transparent focus:border-emerald-500/20"
                                      aria-label={t('product.gtin_label', { defaultValue: 'GTIN' })}
                                      value={gtin || ''} 
                                      onChange={e => setGtin(e.target.value || null)} 
                                      placeholder="00000000000000"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Intelligence Metadata */}
                              <div className="p-5 bg-indigo-50/50 rounded-[2rem] border border-indigo-100/50 space-y-4">
                                <div className="flex items-center gap-2 mb-1">
                                  <BrainCircuit size={14} className="text-indigo-500" />
                                  <h5 className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">{t('product.intelligence_metadata', 'Intelligence Metadata')}</h5>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-1">
                                    <label className="text-[8px] font-black text-indigo-400 uppercase tracking-widest px-1">{t('product.expiration_label')}</label>
                                    <button 
                                      type="button"
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowDatePicker(true); }}
                                      className="w-full bg-white rounded-xl px-4 py-2 text-xs font-bold text-indigo-900 outline-none border-2 border-transparent focus:border-indigo-500/20 text-left flex items-center justify-between"
                                    >
                                      <span>{formatDisplayDate(expirationDate)}</span>
                                      <CalendarIcon size={12} className="text-indigo-400" />
                                    </button>
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-[8px] font-black text-indigo-400 uppercase tracking-widest px-1">{t('product.shelf_life_label', 'Shelf Life (Days)')}</label>
                                    <input 
                                      type="number"
                                      className="w-full bg-white rounded-xl px-4 py-2 text-xs font-bold text-indigo-900 outline-none border-2 border-transparent focus:border-indigo-500/20"
                                      min={0} max={36500} step={1} aria-label={t('product.shelf_life_label', { defaultValue: 'Shelf Life (Days)' })}
                                      value={shelfLife ?? ''}
                                      onChange={e => {
                                        const val = e.target.value ? parseInt(e.target.value) : null;
                                        setShelfLife(val);
                                        if (val !== null) {
                                          const date = new Date((item as any).created_at || new Date());
                                          date.setDate(date.getDate() + val);
                                          setExpirationDate(toLocalISO(date));
                                        } else {
                                          setExpirationDate(null);
                                        }
                                      }} 
                                      placeholder="30"
                                    />
                                  </div>
                                </div>
                              </div>

                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <AnimatePresence>
                    {showDatePicker && (
                      <ExpirationDatePicker 
                        currentValue={expirationDate}
                        onClose={() => setShowDatePicker(false)}
                        onSave={(date) => {
                          const dateStr = toLocalISO(date);
                          setExpirationDate(dateStr);
                          
                          const startDate = new Date((item as any).created_at || new Date());
                          // Normalize to midnight for accurate day diff
                          const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                          const end = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                          
                          const diffTime = end.getTime() - start.getTime();
                          const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                          setShelfLife(Math.max(0, diffDays));
                          setShowDatePicker(false);
                        }}
                        minDate={new Date((item as any).created_at || new Date())}
                      />
                    )}
                  </AnimatePresence>
                </div>

                {/* Footer */}
                <div className="px-6 py-6 bg-gray-50/50 flex gap-3 items-center border-t border-gray-100">
                  <TooltipButton label={t('product.remove_item_title', { defaultValue: 'Remove item' })}
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="w-12 h-12 bg-rose-500 text-white rounded-xl shadow-lg shadow-rose-200 hover:bg-rose-600 transition-all active:scale-95 flex items-center justify-center flex-shrink-0"
                  >
                    <Trash2 size={20} />
                  </TooltipButton>
                  {inline && <button type="button" onClick={onClose} className="min-h-12 rounded-2xl border border-white/10 px-3 text-xs text-slate-200">
                    {t('common.cancel', { defaultValue: 'Cancel' })}
                  </button>}
                  <button
                    type="submit"
                    className="flex-1 bg-emerald-500 text-white h-12 rounded-xl font-black text-sm uppercase tracking-widest shadow-lg shadow-emerald-200 hover:bg-emerald-600 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                  >
                    <Check size={18} strokeWidth={3} />
                    <span>{t('product.update_item')}</span>
                  </button>
                </div>
              </form>
            </motion.div>

            <ConfirmationModal 
              isOpen={showDeleteConfirm}
              onClose={() => setShowDeleteConfirm(false)}
              onConfirm={() => { onRemove(item!.id); onClose(); }}
              title={t('product.remove_item_title')}
              message={t('product.remove_item_desc', { name: displayName })}
              confirmLabel={t('product.yes_remove')}
              cancelLabel={t('product.keep_item')}
              variant="danger"
            />
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
